import { createClient } from "@/lib/supabase/client";
import { loadNotificationPreferences, canSendSMS,  } from "@/lib/notificationPreferences";

const PORTAL_URL = "http://www.premierpropertymgt.net";

/**
 * Low-level SMS sender — calls the send-sms Edge Function.
 * Non-blocking: errors are logged but never thrown.
 */
export async function sendSMS(
  to: string,
  message: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const supabase = createClient();
    const { error } = await supabase.functions.invoke("send-sms", {
      body: { to, message },
    });
    if (error) {
      console.warn("[sendSMS] Edge function error:", error.message);
      return { success: false, error: error.message };
    }
    return { success: true };
  } catch (err) {
    const msg = err instanceof Error ? err.message : "Unknown error";
    console.warn("[sendSMS] Failed:", msg);
    return { success: false, error: msg };
  }
}

// ─── Guard-checked SMS helpers ───
// Each helper loads prefs for the recipient user, checks canSendSMS(), then fires.

/**
 * SMS to admin when a new lead is submitted.
 * Loads admin prefs by role='admin' lookup.
 */
export async function smsAdminNewLead(data: {
  name: string;
  phone: string;
  serviceType: string;
  bedrooms?: string;
  urgencyScore?: number;
  adminUserId: string;
  adminPhone: string;
}): Promise<void> {
  const prefs = await loadNotificationPreferences(data.adminUserId, "admin");
  if (!canSendSMS(prefs, "adminNewLead")) return;
  const bedroomsPart = data.bedrooms ? ` | ${data.bedrooms} BR` : "";
  const scorePart = data.urgencyScore != null ? ` | Urgency: ${data.urgencyScore}/10` : "";
  const msg =
    `[Premier Property] New lead: ${data.name} | ${data.serviceType}${bedroomsPart}${scorePart} | ${data.phone}. ` +
    `View: ${PORTAL_URL}/admin-dashboard`;
  await sendSMS(data.adminPhone, msg);
}

/**
 * SMS to lead when their status changes to 'contacted' or 'signed'.
 */
export async function smsLeadStatusChange(data: {
  name: string;
  phone: string;
  serviceType: string;
  newStatus: string;
}): Promise<void> {
  if (data.newStatus !== "contacted" && data.newStatus !== "signed") return;
  const msgMap: Record<string, string> = {
    contacted:
      `[Premier Property] Hi ${data.name}, our team has reached out regarding your ${data.serviceType} inquiry. ` +
      `Questions? Call (855) 596-3948.`,
    signed:
      `[Premier Property] Welcome, ${data.name}! You're now a Premier client. ` +
      `Access your portal: ${PORTAL_URL}/owner-dashboard`,
  };
  await sendSMS(data.phone, msgMap[data.newStatus]);
}

/**
 * SMS to admin when a new cleaner application is submitted.
 */
export async function smsAdminNewCleanerApplication(data: {
  name: string;
  phone: string;
  experienceLevel?: number;
  availability?: string[];
  adminUserId: string;
  adminPhone: string;
}): Promise<void> {
  const prefs = await loadNotificationPreferences(data.adminUserId, "admin");
  if (!canSendSMS(prefs, "adminCleanerApplication")) return;
  const expPart = data.experienceLevel != null ? ` | Exp: ${data.experienceLevel}/10` : "";
  const availPart =
    data.availability && data.availability.length > 0
      ? ` | Avail: ${data.availability.slice(0, 3).map((d) => d.slice(0, 3)).join("/")}${data.availability.length > 3 ? "+" : ""}`
      : "";
  const msg =
    `[Premier Property] New cleaner application from ${data.name}${expPart}${availPart} | ${data.phone}. ` +
    `Review: ${PORTAL_URL}/admin-dashboard`;
  await sendSMS(data.adminPhone, msg);
}

/**
 * SMS to cleaner when their application status changes to accepted or rejected.
 */
export async function smsCleanerStatusChange(data: {
  name: string;
  phone: string;
  newStatus: string;
}): Promise<void> {
  if (data.newStatus !== "hired" && data.newStatus !== "rejected") return;
  const msgMap: Record<string, string> = {
    hired:
      `[Premier Property] Congratulations ${data.name}! Your cleaner application has been accepted. ` +
      `Our manager will call you within 24 hours.`,
    rejected:
      `[Premier Property] Hi ${data.name}, thank you for applying. After review, we've moved forward with other candidates. ` +
      `We appreciate your interest.`,
  };
  await sendSMS(data.phone, msgMap[data.newStatus]);
}

/**
 * SMS to owner when service request status changes.
 */
export async function smsOwnerServiceRequestUpdate(data: {
  ownerUserId: string;
  ownerPhone: string;
  title: string;
  newStatus: string;
  propertyName?: string;
}): Promise<void> {
  const prefs = await loadNotificationPreferences(data.ownerUserId, "owner");
  if (!canSendSMS(prefs, "ownerServiceRequestUpdate")) return;
  const statusLabel: Record<string, string> = {
    open: "Submitted",
    in_progress: "In Progress",
    resolved: "Completed",
    closed: "Closed",
  };
  const label = statusLabel[data.newStatus] ?? data.newStatus;
  const property = data.propertyName ? ` (${data.propertyName})` : "";
  const msg =
    `[Premier Property] Service request "${data.title}"${property} is now ${label}. ` +
    `View: ${PORTAL_URL}/owner-dashboard`;
  await sendSMS(data.ownerPhone, msg);
}

/**
 * SMS to owner when a new booking is confirmed.
 */
export async function smsOwnerBookingConfirmed(data: {
  ownerUserId: string;
  ownerPhone: string;
  propertyName: string;
  guestName: string;
  checkIn: string;
  checkOut: string;
}): Promise<void> {
  const prefs = await loadNotificationPreferences(data.ownerUserId, "owner");
  if (!canSendSMS(prefs, "ownerBookingConfirmation")) return;
  const msg =
    `[Premier Property] Booking confirmed at ${data.propertyName}: ${data.guestName}, ` +
    `${data.checkIn} – ${data.checkOut}. View: ${PORTAL_URL}/owner-dashboard`;
  await sendSMS(data.ownerPhone, msg);
}

/**
 * SMS to owner when a scheduled cleaning is 24 hrs away.
 */
export async function smsOwnerCleaningReminder(data: {
  ownerUserId: string;
  ownerPhone: string;
  propertyName: string;
  cleaningType: string;
  scheduledDate: string;
  scheduledTime: string;
}): Promise<void> {
  const prefs = await loadNotificationPreferences(data.ownerUserId, "owner");
  if (!canSendSMS(prefs, "ownerCleaningReminder")) return;
  const msg =
    `[Premier Property] Reminder: ${data.cleaningType} at ${data.propertyName} is scheduled ` +
    `tomorrow at ${data.scheduledTime} (${data.scheduledDate}). View: ${PORTAL_URL}/owner-dashboard`;
  await sendSMS(data.ownerPhone, msg);
}

/**
 * SMS to owner when admin sends a message reply.
 */
export async function smsOwnerAdminReply(data: {
  ownerUserId: string;
  ownerPhone: string;
  ownerName?: string;
  messagePreview: string;
}): Promise<void> {
  const prefs = await loadNotificationPreferences(data.ownerUserId, "owner");
  if (!canSendSMS(prefs, "ownerAdminReply")) return;
  const greeting = data.ownerName ? `Hi ${data.ownerName}, ` : "";
  const preview = data.messagePreview.length > 80
    ? data.messagePreview.slice(0, 77) + "..."
    : data.messagePreview;
  const msg =
    `[Premier Property] ${greeting}you have a new message from your property manager: "${preview}" ` +
    `View: ${PORTAL_URL}/owner-dashboard`;
  await sendSMS(data.ownerPhone, msg);
}

// ─── Utility: fetch admin notification prefs (for admin SMS phone) ───
export async function getAdminSMSPrefs(): Promise<{ userId: string; phone: string } | null> {
  try {
    const supabase = createClient();
    const { data } = await supabase
      .from("notification_preferences")
      .select("user_id, sms_phone, sms_enabled")
      .eq("role", "admin")
      .eq("sms_enabled", true)
      .maybeSingle();
    if (!data || !data.sms_phone) return null;
    return { userId: data.user_id as string, phone: data.sms_phone as string };
  } catch {
    return null;
  }
}

// ─── Utility: fetch owner SMS phone by owner_id ───
export async function getOwnerSMSPhone(
  ownerUserId: string
): Promise<string | null> {
  try {
    const supabase = createClient();
    const { data } = await supabase
      .from("notification_preferences")
      .select("sms_phone, sms_enabled")
      .eq("user_id", ownerUserId)
      .eq("sms_enabled", true)
      .maybeSingle();
    if (!data || !data.sms_phone) return null;
    return data.sms_phone as string;
  } catch {
    return null;
  }
}
